/**
 * Modal to fill out a screening form for a screening-stage episode (contextual Action).
 * Group Therapy rows use the Group screening note QuestionnaireResponse;
 * other service types keep legacy outcomes (UI-only until high-risk note is wired).
 */

import { Button, Group, Modal, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useState } from 'react';
import { practitionerPreferredReference } from '@/lib/loadPractitionerPreferredLabels';
import {
  GROUP_SCREENING_OUTCOME_CODES,
  GROUP_SCREENING_OUTCOME_LABELS,
  saveGroupScreeningQuestionnaireResponse,
  type GroupScreeningOutcomeCode,
} from '@/lib/groupTherapy';
import type { StageRow } from './StageTab';

export interface ScreeningFormModalProps {
  open: boolean;
  onClose: () => void;
  row: StageRow | null;
  onSaved?: () => void;
}

const LEGACY_OUTCOME_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'reschedule', label: 'Reschedule' },
  { value: 'inactive', label: 'Move to inactive' },
];

const GROUP_OUTCOME_OPTIONS = GROUP_SCREENING_OUTCOME_CODES.map((code) => ({
  value: code,
  label: GROUP_SCREENING_OUTCOME_LABELS[code],
}));

function isGroupTherapyRow(row: StageRow | null): boolean {
  return (row as { episodeServiceType?: string } | null)?.episodeServiceType === 'Group Therapy';
}

function isGroupOutcome(v: string): v is GroupScreeningOutcomeCode {
  return (GROUP_SCREENING_OUTCOME_CODES as readonly string[]).includes(v);
}

export function ScreeningFormModal({
  open,
  onClose,
  row,
  onSaved,
}: ScreeningFormModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [outcome, setOutcome] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [destinationTemplateReference, setDestinationTemplateReference] = useState('');
  const [destinationGroupId, setDestinationGroupId] = useState('');
  const [saving, setSaving] = useState(false);

  const isGroupTherapy = isGroupTherapyRow(row);
  const outcomeOptions = isGroupTherapy ? GROUP_OUTCOME_OPTIONS : LEGACY_OUTCOME_OPTIONS;
  const showDestinationProgram =
    isGroupTherapy && outcome === 'appropriate-different-group';

  const handleClose = (): void => {
    setOutcome(null);
    setNotes('');
    setDestinationTemplateReference('');
    setDestinationGroupId('');
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!row || !outcome) return;

    if (isGroupTherapy) {
      if (!isGroupOutcome(outcome)) return;
      const patientId = String((row as { clientId?: string }).clientId ?? '');
      const serviceRequestId = String((row as { id?: string }).id ?? '');
      const episodeOfCareId = (row as { episodeOfCareId?: string }).episodeOfCareId;
      if (!patientId || !serviceRequestId) {
        notifications.show({
          color: 'red',
          title: 'Missing context',
          message: 'Client and ServiceRequest are required to save a Group screening note.',
        });
        return;
      }
      setSaving(true);
      try {
        await saveGroupScreeningQuestionnaireResponse(medplum, {
          patientId,
          serviceRequestId,
          episodeOfCareId,
          outcome,
          notes,
          destinationTemplateReference: showDestinationProgram
            ? destinationTemplateReference
            : undefined,
          destinationGroupId: showDestinationProgram
            ? destinationGroupId.trim() || undefined
            : undefined,
          author: profile
            ? profile.resourceType === 'Practitioner'
              ? practitionerPreferredReference(profile)
              : createReference(profile)
            : undefined,
        });
        notifications.show({
          color: 'green',
          title: 'Screening saved',
          message: 'Group screening note recorded.',
        });
        handleClose();
        onSaved?.();
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Save failed',
          message: err instanceof Error ? err.message : 'Could not save screening note.',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    handleClose();
    onSaved?.();
  };

  return (
    <Modal opened={open} onClose={handleClose} title="Screening" size="md">
      <Stack gap="md">
        {row && (
          <Stack gap="xs">
            <span><strong>Client</strong> {(row as { clientName?: string }).clientName ?? '—'}</span>
            <span><strong>Service type</strong> {(row as { episodeServiceType?: string }).episodeServiceType ?? '—'}</span>
          </Stack>
        )}
        <Select
          label="Outcome"
          placeholder="Select outcome"
          data={outcomeOptions}
          value={outcome}
          onChange={setOutcome}
        />
        {showDestinationProgram && (
          <>
            <TextInput
              label="Destination Group Template"
              description="HealthcareService id or HealthcareService/{id} for the destination template"
              placeholder="HealthcareService/{id}"
              value={destinationTemplateReference}
              onChange={(e) => setDestinationTemplateReference(e.currentTarget.value)}
            />
            <TextInput
              label="Destination Group (optional)"
              description="Concrete cohort id when known (Group/{id} or id)"
              placeholder="Group id"
              value={destinationGroupId}
              onChange={(e) => setDestinationGroupId(e.currentTarget.value)}
            />
          </>
        )}
        <Textarea
          label="Screening notes"
          placeholder="Screening notes"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={3}
        />
        <Group mt="md" justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={!outcome}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
